import { useLocation, useNavigation, useSearchParams } from '@remix-run/react'
import { motion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PlentyBody from '#app/components/typography/plenty-body'
import PlentyHeading from '#app/components/typography/plenty-heading'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion'
import { Button } from '#app/components/ui/button'
import {
	Sheet,
	SheetContent,
	SheetFooter,
	SheetTrigger,
} from '#app/components/ui/sheet'
import useHeaderTop from '#app/hooks/use-header-top'
import { cn } from '#app/utils/misc'
import AppliedFilters from '../../applied-filters'
import { FilterButton } from '../filter-button'
import { BrandSelection } from './brand-selection'
import ColourSelection from './colour-selection'
import { GenderSelection } from './gender-selection'
import { MaterialsSelection } from './materials-selection'
import { SizeSelection } from './size-selection'
import SortSelection from './sort-selection'

const FilterAndSortSheet = () => {
	const [open, setOpen] = useState(false)
	const navigation = useNavigation()
	const top = useHeaderTop()
	const [isSticky, setIsSticky] = useState(false)
	const filterRef = useRef<HTMLDivElement | null>(null)
	const location = useLocation()
	const [, setSearchParams] = useSearchParams()
	const isKids = location.pathname.includes('kids') ?? false
	let { t } = useTranslation('product_list_page')
	function getElementTop(element: HTMLDivElement) {
		const rect = element.getBoundingClientRect()

		if ('visualViewport' in window && window.visualViewport) {
			return rect.top + window.visualViewport.offsetTop
		} else {
			return rect.top
		}
	}

	const handleScroll = useCallback(() => {
		if (!filterRef.current) return

		const elTop = getElementTop(filterRef.current)
		if (elTop <= top) {
			setIsSticky(true)
		} else {
			setIsSticky(false)
		}
	}, [top])

	useEffect(() => {
		if (!window) return

		window.addEventListener('scroll', handleScroll)

		return () => window.removeEventListener('scroll', handleScroll)
	}, [handleScroll])

	useEffect(() => {
		handleScroll()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [top, filterRef])

	const getSelectedCount = () => {
		const searchParams = new URLSearchParams(
			navigation.state === 'idle'
				? location.search
				: navigation.location.search,
		)
		const brandCount = searchParams.getAll('brand').length
		const colorCount = searchParams.getAll('color').length
		const sizeCount = searchParams.getAll('size').length
		const genderCount = searchParams.getAll('gender').length
		const materialsCount = searchParams.getAll('materials').length
		const categoryCount = searchParams.getAll('category').length
		return (
			brandCount +
			colorCount +
			sizeCount +
			genderCount +
			categoryCount +
			materialsCount
		)
	}

	const resetFilters = () => {
		// Get the current search parameters
		const currentParams = new URLSearchParams(location.search)
		// Create a new URLSearchParams object to store the filtered parameters
		const newParams = new URLSearchParams()

		// Iterate through the current parameters
		currentParams.forEach((value, key) => {
			// Keep only the parameters with the key "store" or "query"
			if (key === 'store' || key === 'query') {
				newParams.append(key, value)
			}
		})

		// Set the new filtered parameters
		setSearchParams(newParams)
	}

	return (
		<motion.div
			className="sticky z-40 h-auto bg-white transition-all md:hidden"
			style={{ top: top - 1, padding: isSticky ? 0 : '0 16px' }}
			variants={{
				hidden: { opacity: 1 },
				visible: { opacity: 1 },
			}}
			initial="hidden"
			animate={isSticky ? 'visible' : 'hidden'}
			ref={filterRef}
		>
			<Sheet open={open} onOpenChange={setOpen}>
				<SheetTrigger asChild className="">
					<FilterButton
						className={cn(
							'transition-all',
							isSticky ? 'rounded-none border-x-0 border-b border-t-0' : '',
						)}
						selectedCount={getSelectedCount()}
						text={t('search:filter_label')}
						showChevron={false}
					/>
				</SheetTrigger>
				<SheetContent className="flex h-full flex-col">
					<PlentyHeading as="h4">{t('search:filter_label')}</PlentyHeading>
					<AppliedFilters withoutReset />
					<div className="flex flex-1 flex-col overflow-y-auto scrollbar-hide">
						<Accordion
							type="single"
							className="mt-6 w-full"
							collapsible
							defaultValue="sort"
						>
							<AccordionItem className="border-b pb-2" value="colour">
								<AccordionTrigger className="flex items-center justify-between">
									<PlentyBody size="lg">{t('common:colour_header')}</PlentyBody>
								</AccordionTrigger>
								<AccordionContent className="flex flex-col gap-2 py-2">
									<ColourSelection />
								</AccordionContent>
							</AccordionItem>

							<AccordionItem className="border-b pb-2" value="size">
								<AccordionTrigger className="flex w-full items-center justify-between ">
									<PlentyBody size="lg">{t('common:size')}</PlentyBody>
								</AccordionTrigger>
								<AccordionContent>
									<SizeSelection />
								</AccordionContent>
							</AccordionItem>

							<AccordionItem className="border-b pb-2" value="brand">
								<AccordionTrigger className="flex w-full items-center justify-between ">
									<PlentyBody size="lg">{t('brand')}</PlentyBody>
								</AccordionTrigger>
								<AccordionContent>
									<BrandSelection />
								</AccordionContent>
							</AccordionItem>
							<AccordionItem className="border-b pb-2" value="material">
								<AccordionTrigger className="flex w-full items-center justify-between ">
									<PlentyBody size="lg">{t('materials')}</PlentyBody>
								</AccordionTrigger>
								<AccordionContent>
									<MaterialsSelection />
								</AccordionContent>
							</AccordionItem>

							{isKids && (
								<AccordionItem className="border-b pb-2" value="gender">
									<AccordionTrigger className="flex w-full items-center justify-between ">
										<PlentyBody size="lg">{t('gender')}</PlentyBody>
									</AccordionTrigger>
									<AccordionContent className="py-2">
										<GenderSelection />
									</AccordionContent>
								</AccordionItem>
							)}
							<AccordionItem className="border-b pb-2" value="sort">
								<AccordionTrigger className="flex w-full items-center justify-between ">
									<PlentyBody size="lg">{t('search:sort_by')}</PlentyBody>
								</AccordionTrigger>
								<AccordionContent className="py-2">
									<SortSelection />
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					</div>
					<SheetFooter className="flex-col">
						<Button size="primary" onClick={() => setOpen(false)}>
							{t('filter_sheet_show_products')}
						</Button>
						<Button
							variant="ghost"
							size="secondary"
							fullWidth
							onClick={() => {
								resetFilters()
								setOpen(false)
							}}
						>
							{t('search:reset_filters')}
						</Button>
					</SheetFooter>
				</SheetContent>
			</Sheet>
		</motion.div>
	)
}

export default FilterAndSortSheet
